import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Highlighter from 'react-highlight-words'

import { getViewActions } from '../../../../utils/actions'
import { buildIndex } from '../../../../utils/indexing'
import { formatValue } from '../../../../utils/formatting'
import { fetchViewData, getViewData } from '../../../../ducks/data'
import { fetchActions, getActions } from '../../../../ducks/actions'
import { getTable, fetchDatabases } from '../../../../ducks/databases'
import Card from '../../../Shared/Card'
import Button from '../../../Shared/Button'
import Checkbox from '../../../Shared/Forms/Checkbox'
import EmptyState from '../../../Shared/EmptyState'
import ActionMenu from '../ActionMenu'
import SelectionHeader from './SelectionHeader'

import styles from './List.module.css'

const FormattedValue = function FormattedValue({ value, column, query }) {
  const formattedValue = formatValue(value, column, false)

  return (
    <Highlighter
      autoEscape
      textToHighlight={formattedValue}
      searchWords={query ? query.split(/\s+/g) : []}
      highlightClassName={styles.searchHighlight}
    />
  )
}

const InfoText = function InfoText({ record, infoText, query, columns }) {
  const children = (infoText || []).map((itm, i) => {
    if (itm && itm.column) {
      return (
        <FormattedValue
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          value={record[itm.column]}
          query={query}
          column={columns[itm.column]}
        />
      )
    }

    // eslint-disable-next-line react/no-array-index-key
    return <FormattedValue key={i} value={itm} query={query} />
  })

  return <p className={styles.infoText}>{children}</p>
}

const Item = function ListItem({
  record,
  view,
  actions,
  query,
  selected,
  handleSelect,
  columns,
}) {
  const { labelColumn, infoText } = view.body

  return (
    <div className={styles.item}>
      <div className={styles.overflow}>
        <ActionMenu
          record={record}
          view={view}
          actions={actions}
          baseURL={`/databases/views/${view.id}`}
        />
      </div>
      <div className={styles.checkbox}>
        <Checkbox small value={!!selected} onChange={handleSelect} />
      </div>
      <h2>
        <Link to={`/databases/views/${view.id}/records/${record.__pk}`}>
          {record[labelColumn] ? (
            <FormattedValue
              value={record[labelColumn]}
              query={query}
              column={columns[labelColumn]}
            />
          ) : (
            'Untitled'
          )}
        </Link>
      </h2>
      <InfoText
        infoText={infoText}
        record={record}
        query={query}
        columns={columns}
      />
    </div>
  )
}

export default function ViewList({ view, query }) {
  const [loading, setLoading] = useState(false)
  const [selection, setSelection] = useState([])
  const dispatch = useDispatch()
  const data = useSelector(getViewData(view.id, query))
  const allActions = useSelector(getActions(view.OrganizationId))

  const selectionMap = buildIndex(selection, itm => itm)
  const { databaseId, table: tableName } = view.body
  const table = useSelector(getTable(databaseId, tableName))

  // Load view data + search
  // Refresh after closing modal
  useEffect(() => {
    dispatch(fetchViewData(view.id, { query }))
  }, [dispatch, view.id, query, window.location.pathname])

  // Load database
  useEffect(() => {
    if (databaseId) {
      dispatch(fetchDatabases(view.OrganizationId))
    }
  }, [dispatch, databaseId])

  // Load actions
  useEffect(() => {
    dispatch(fetchActions(view.OrganizationId))
  }, [dispatch, view.id])

  const loadMore = useCallback(async () => {
    const lastRecord = data[data.length - 1]

    setLoading(true)

    try {
      await dispatch(
        fetchViewData(view.id, {
          query,
          offset: data.length,
          previousId: lastRecord && lastRecord.__pk,
        })
      )
    } catch (err) {
      // Do something
    }

    setLoading(false)
  }, [dispatch, data, view])

  const handleClickCheckbox = id => () => {
    let newSelection

    if (selectionMap[id]) {
      newSelection = selection.filter(itm => itm !== id)
    } else {
      newSelection = selection.concat([id])
    }

    setSelection(newSelection)
  }

  if (!data || !table) {
    return <EmptyState loading />
  }

  const actions = getViewActions(view, allActions)

  const displayData = data.filter(itm => itm)
  const columns = buildIndex(table.columns, c => c.name)

  return (
    <div className={styles.list}>
      <Card noPad>
        <SelectionHeader
          items={displayData}
          selection={selection}
          setSelection={setSelection}
          actions={actions}
        />
        {displayData.map(itm => (
          <Item
            key={itm.__pk}
            record={itm}
            view={view}
            actions={actions}
            query={query}
            selected={selectionMap[itm.__pk]}
            handleSelect={handleClickCheckbox(itm.__pk)}
            columns={columns}
          />
        ))}
        {displayData.length === 0 && (
          <EmptyState tight>
            <h2>No items found</h2>
          </EmptyState>
        )}
      </Card>
      {displayData.length >= 50 &&
        (loading ? (
          <EmptyState className={styles.loader} loading />
        ) : (
          <EmptyState className={styles.loader}>
            <Button color="grey" onClick={loadMore}>
              Load More
            </Button>
          </EmptyState>
        ))}
    </div>
  )
}
